import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ShadowGuardApi, type LoginResponse } from './api';

export interface TenantInfo {
  id: string;
  label: string;
  domain: string;
  controlPlaneUrl: string;
}

/** Entry from ~/.shadowguard/tenants.json or dashboard-vault.json */
export interface VaultTenantInput {
  tenantId: string;
  label?: string;
  domain?: string;
  apiKey: string;
  controlPlaneUrl?: string;
  agentId?: string;
}

interface AuthState {
  api: ShadowGuardApi | null;
  tenant: LoginResponse['tenant'] | null;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  allTenants: TenantInfo[];
  activeTenantId?: string;
  /** True when durable local credentials exist for at least one tenant */
  hasSavedVault: boolean;
}

interface AuthContextValue extends AuthState {
  connect: (controlPlaneUrl: string, apiKey: string, meta?: { domain?: string }) => Promise<void>;
  disconnect: () => void;
  /** Remove active session only — keeps local multi-project vault for next visit */
  switchTenant: (tenantId: string) => Promise<void>;
  addTenant: (tenant: TenantInfo) => void;
  /** Import all projects from local tenants.json / vault file; log into each */
  importVault: (
    entries: VaultTenantInput[],
    options?: { controlPlaneUrl?: string; activateTenantId?: string }
  ) => Promise<{ ok: number; failed: Array<{ label: string; error: string }> }>;
  /** One-click reconnect using durable vault (optional tenant id) */
  connectSaved: (tenantId?: string) => Promise<void>;
  /** Wipe durable vault + session (full sign-out of all projects on this browser) */
  clearVault: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const LEGACY_STORAGE_KEY = 'shadowguard_config';
const TENANTS_KEY = 'shadowguard_tenants';
const SESSION_KEY = 'shadowguard_session';
const SESSION_CREDENTIALS_KEY = 'shadowguard_session_credentials';
/** Durable credentials for multi-project local access (this browser only) */
const VAULT_CREDENTIALS_KEY = 'shadowguard_vault_credentials';

const DEFAULT_CP = 'https://shadowguard-control-plane-v2.simeon-garratt.workers.dev';

interface StoredSession {
  controlPlaneUrl: string;
  apiKey: string;
  token: string;
  tenant: LoginResponse['tenant'];
  activeTenantId?: string;
}

interface StoredCredential {
  apiKey: string;
  controlPlaneUrl: string;
  domain?: string;
  label?: string;
}

type CredentialMap = Record<string, StoredCredential>;

function parseJson<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function loadTenants(): TenantInfo[] {
  return parseJson<TenantInfo[]>(localStorage.getItem(TENANTS_KEY), []);
}

function saveTenants(tenants: TenantInfo[]): void {
  localStorage.setItem(TENANTS_KEY, JSON.stringify(tenants));
}

/**
 * Rebuild switcher list from vault credentials only.
 * Drops legacy labels ("simeon website", "SCAFU") and duplicate org rows.
 * Prefer domain as display label when present.
 */
function reconcileTenantsFromVault(): TenantInfo[] {
  const creds = loadVaultCredentials();
  const previous = loadTenants();
  const byId = new Map(previous.map((t) => [t.id, t]));
  const next: TenantInfo[] = [];

  for (const [id, c] of Object.entries(creds)) {
    const prev = byId.get(id);
    const domain = c.domain || prev?.domain || c.label || id.slice(0, 8);
    // Prefer domain-shaped labels over free-text nicknames
    const label =
      (c.domain && c.domain.includes('.') ? c.domain : null) ||
      (c.label && c.label.includes('.') ? c.label : null) ||
      c.domain ||
      c.label ||
      prev?.label ||
      id.slice(0, 8);
    next.push({
      id,
      label,
      domain,
      controlPlaneUrl: c.controlPlaneUrl || prev?.controlPlaneUrl || DEFAULT_CP,
    });
  }

  // Stable sort by label
  next.sort((a, b) => a.label.localeCompare(b.label));
  saveTenants(next);
  return next;
}

function loadSession(): StoredSession | null {
  return parseJson<StoredSession | null>(sessionStorage.getItem(SESSION_KEY), null);
}

function saveSession(session: StoredSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

function loadVaultCredentials(): CredentialMap {
  // Prefer durable vault; fall back to session-only (legacy)
  const durable = parseJson<CredentialMap>(localStorage.getItem(VAULT_CREDENTIALS_KEY), {});
  if (Object.keys(durable).length > 0) return durable;
  return parseJson<CredentialMap>(sessionStorage.getItem(SESSION_CREDENTIALS_KEY), {});
}

function saveVaultCredentials(map: CredentialMap): void {
  localStorage.setItem(VAULT_CREDENTIALS_KEY, JSON.stringify(map));
  // Keep session mirror for in-tab switcher path
  sessionStorage.setItem(SESSION_CREDENTIALS_KEY, JSON.stringify(map));
}

function upsertTenant(tenant: TenantInfo): TenantInfo[] {
  const existing = loadTenants();
  const idx = existing.findIndex((t) => t.id === tenant.id);
  if (idx >= 0) {
    existing[idx] = tenant;
  } else {
    existing.push(tenant);
  }
  saveTenants(existing);
  return existing;
}

function decodeExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function hasAnyVault(): boolean {
  return Object.keys(loadVaultCredentials()).length > 0;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<AuthState>({
    api: null,
    tenant: null,
    isConnected: false,
    isLoading: true,
    error: null,
    allTenants: [],
    activeTenantId: undefined,
    hasSavedVault: false,
  });

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback((session: StoredSession) => {
    clearRefreshTimer();

    const expiresAt = decodeExp(session.token);
    if (!expiresAt) return;

    const refreshAt = Math.max(0, expiresAt - Date.now() - 5 * 60 * 1000);
    if (refreshAt <= 0) return;

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const api = new ShadowGuardApi(session.controlPlaneUrl);
        const res = await api.login(session.apiKey);
        api.setToken(res.token);

        const next: StoredSession = {
          ...session,
          token: res.token,
          tenant: res.tenant,
        };
        saveSession(next);

        const currentCreds = loadVaultCredentials();
        currentCreds[res.tenant.id] = {
          apiKey: session.apiKey,
          controlPlaneUrl: session.controlPlaneUrl,
          label: currentCreds[res.tenant.id]?.label || res.tenant.name,
          domain: currentCreds[res.tenant.id]?.domain,
        };
        saveVaultCredentials(currentCreds);

        setState((s) => ({
          ...s,
          api,
          tenant: res.tenant,
          isConnected: true,
          error: null,
          activeTenantId: res.tenant.id,
          hasSavedVault: true,
        }));

        scheduleTokenRefresh(next);
      } catch {
        clearSession();
        setState((s) => ({
          ...s,
          api: null,
          tenant: null,
          isConnected: false,
          error: 'Session refresh failed. Use saved projects or reconnect.',
          hasSavedVault: hasAnyVault(),
        }));
      }
    }, refreshAt);
  }, [clearRefreshTimer]);

  useEffect(() => {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    localStorage.removeItem('sg_pin_api_key');
    localStorage.removeItem('sg_pin_url');

    // Migrate session credentials into durable vault once
    const sessionCreds = parseJson<CredentialMap>(
      sessionStorage.getItem(SESSION_CREDENTIALS_KEY),
      {}
    );
    const durable = parseJson<CredentialMap>(localStorage.getItem(VAULT_CREDENTIALS_KEY), {});
    if (Object.keys(sessionCreds).length > 0 && Object.keys(durable).length === 0) {
      saveVaultCredentials(sessionCreds);
    }

    // Always prune switcher to vault credentials (drops "simeon website" etc.)
    const allTenants = reconcileTenantsFromVault();
    const stored = loadSession();
    const vaultPresent = hasAnyVault();

    if (!stored) {
      // Auto-reconnect from durable multi-project vault (no session yet)
      if (vaultPresent) {
        const creds = loadVaultCredentials();
        const preferId =
          allTenants[0]?.id && creds[allTenants[0].id]
            ? allTenants[0].id
            : Object.keys(creds)[0];
        const credential = preferId ? creds[preferId] : null;

        if (credential) {
          (async () => {
            try {
              const reconciled = reconcileTenantsFromVault();
              const api = new ShadowGuardApi(credential.controlPlaneUrl);
              const res = await api.login(credential.apiKey);
              api.setToken(res.token);
              const session: StoredSession = {
                controlPlaneUrl: credential.controlPlaneUrl,
                apiKey: credential.apiKey,
                token: res.token,
                tenant: res.tenant,
                activeTenantId: preferId,
              };
              saveSession(session);
              setState({
                api,
                tenant: res.tenant,
                isConnected: true,
                isLoading: false,
                error: null,
                allTenants: reconciled,
                activeTenantId: preferId,
                hasSavedVault: true,
              });
              scheduleTokenRefresh(session);
            } catch {
              setState((s) => ({
                ...s,
                isLoading: false,
                allTenants: reconcileTenantsFromVault(),
                hasSavedVault: true,
                error: 'Saved vault present but auto-login failed. Use Open saved projects.',
              }));
            }
          })();
          return () => clearRefreshTimer();
        }
      }

      setState((s) => ({
        ...s,
        isLoading: false,
        allTenants,
        hasSavedVault: vaultPresent,
      }));
      return () => clearRefreshTimer();
    }

    const restore = async () => {
      const api = new ShadowGuardApi(stored.controlPlaneUrl, stored.token);
      try {
        await api.getStats();
        setState({
          api,
          tenant: stored.tenant,
          isConnected: true,
          isLoading: false,
          error: null,
          allTenants: reconcileTenantsFromVault(),
          activeTenantId: stored.activeTenantId || stored.tenant.id,
          hasSavedVault: vaultPresent,
        });
        scheduleTokenRefresh(stored);
      } catch {
        try {
          const refreshedApi = new ShadowGuardApi(stored.controlPlaneUrl);
          const res = await refreshedApi.login(stored.apiKey);
          refreshedApi.setToken(res.token);

          const next: StoredSession = {
            controlPlaneUrl: stored.controlPlaneUrl,
            apiKey: stored.apiKey,
            token: res.token,
            tenant: res.tenant,
            activeTenantId: stored.activeTenantId || res.tenant.id,
          };
          saveSession(next);

          setState({
            api: refreshedApi,
            tenant: res.tenant,
            isConnected: true,
            isLoading: false,
            error: null,
            allTenants: reconcileTenantsFromVault(),
            activeTenantId: next.activeTenantId,
            hasSavedVault: vaultPresent,
          });

          scheduleTokenRefresh(next);
        } catch {
          clearSession();
          setState({
            api: null,
            tenant: null,
            isConnected: false,
            isLoading: false,
            error: vaultPresent
              ? 'Session expired. Click “Open saved projects” to reconnect.'
              : 'Session expired. Reconnect with your API key or import vault.',
            allTenants: reconcileTenantsFromVault(),
            activeTenantId: undefined,
            hasSavedVault: vaultPresent,
          });
        }
      }
    };

    restore();

    return () => clearRefreshTimer();
  }, [clearRefreshTimer, scheduleTokenRefresh]);

  const connect = useCallback(
    async (controlPlaneUrl: string, apiKey: string, meta?: { domain?: string }) => {
      setState((s) => ({ ...s, isLoading: true, error: null }));

      try {
        const api = new ShadowGuardApi(controlPlaneUrl);
        const res = await api.login(apiKey);
        api.setToken(res.token);

        const session: StoredSession = {
          controlPlaneUrl,
          apiKey,
          token: res.token,
          tenant: res.tenant,
          activeTenantId: res.tenant.id,
        };
        saveSession(session);

        const creds = loadVaultCredentials();
        creds[res.tenant.id] = {
          apiKey,
          controlPlaneUrl,
          label: res.tenant.name,
          domain: meta?.domain || res.tenant.name,
        };
        saveVaultCredentials(creds);

        // Single-key connect: still upsert, then reconcile so orphans disappear
        upsertTenant({
          id: res.tenant.id,
          label: meta?.domain || res.tenant.name,
          domain: meta?.domain || res.tenant.name,
          controlPlaneUrl,
        });
        const allTenants = reconcileTenantsFromVault();

        setState({
          api,
          tenant: res.tenant,
          isConnected: true,
          isLoading: false,
          error: null,
          allTenants,
          activeTenantId: res.tenant.id,
          hasSavedVault: true,
        });

        scheduleTokenRefresh(session);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Connection failed';
        setState((s) => ({ ...s, isLoading: false, error: message, hasSavedVault: hasAnyVault() }));
        throw err;
      }
    },
    [scheduleTokenRefresh]
  );

  const disconnect = useCallback(() => {
    clearRefreshTimer();
    clearSession();
    // Keep durable vault so multi-project access survives sign-out
    setState({
      api: null,
      tenant: null,
      isConnected: false,
      isLoading: false,
      error: null,
      allTenants: loadTenants(),
      activeTenantId: undefined,
      hasSavedVault: hasAnyVault(),
    });
  }, [clearRefreshTimer]);

  const clearVault = useCallback(() => {
    clearRefreshTimer();
    clearSession();
    localStorage.removeItem(VAULT_CREDENTIALS_KEY);
    sessionStorage.removeItem(SESSION_CREDENTIALS_KEY);
    localStorage.removeItem(TENANTS_KEY);
    setState({
      api: null,
      tenant: null,
      isConnected: false,
      isLoading: false,
      error: null,
      allTenants: [],
      activeTenantId: undefined,
      hasSavedVault: false,
    });
  }, [clearRefreshTimer]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const tenants = loadTenants();
    const target = tenants.find((t) => t.id === tenantId);
    if (!target) return;

    setState((s) => ({ ...s, isLoading: true, error: null, activeTenantId: tenantId }));

    const creds = loadVaultCredentials();
    const credential = creds[tenantId];

    if (!credential) {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: 'No saved key for this project. Import vault or connect with its API key.',
        hasSavedVault: hasAnyVault(),
      }));
      return;
    }

    try {
      const api = new ShadowGuardApi(credential.controlPlaneUrl);
      const res = await api.login(credential.apiKey);
      api.setToken(res.token);

      const session: StoredSession = {
        controlPlaneUrl: credential.controlPlaneUrl,
        apiKey: credential.apiKey,
        token: res.token,
        tenant: res.tenant,
        activeTenantId: tenantId,
      };
      saveSession(session);

      const allTenants = reconcileTenantsFromVault();

      setState({
        api,
        tenant: res.tenant,
        isConnected: true,
        isLoading: false,
        error: null,
        allTenants,
        activeTenantId: tenantId,
        hasSavedVault: true,
      });

      scheduleTokenRefresh(session);
    } catch {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: 'Failed to switch project. Key may have been rotated — re-import vault.',
        hasSavedVault: hasAnyVault(),
        allTenants: reconcileTenantsFromVault(),
      }));
    }
  }, [scheduleTokenRefresh]);

  const importVault = useCallback(
    async (
      entries: VaultTenantInput[],
      options?: { controlPlaneUrl?: string; activateTenantId?: string }
    ) => {
      setState((s) => ({ ...s, isLoading: true, error: null }));
      const defaultCp = options?.controlPlaneUrl || DEFAULT_CP;
      // Replace (not merge): old "SCAFU" / "simeon website" leftovers cause switcher dupes
      const creds: CredentialMap = {};
      const importedTenants: TenantInfo[] = [];
      const failed: Array<{ label: string; error: string }> = [];
      let ok = 0;
      let firstOk: { session: StoredSession; tenant: LoginResponse['tenant']; domain: string } | null =
        null;

      for (const entry of entries) {
        const displayLabel = entry.domain || entry.label || entry.tenantId.slice(0, 8);
        const cp = entry.controlPlaneUrl || defaultCp;
        try {
          const api = new ShadowGuardApi(cp);
          const res = await api.login(entry.apiKey);
          if (entry.tenantId && res.tenant.id !== entry.tenantId) {
            failed.push({
              label: displayLabel,
              error: `Key maps to tenant ${res.tenant.id}, expected ${entry.tenantId}`,
            });
            continue;
          }
          const domain = entry.domain || res.tenant.name;
          // Prefer domain as switcher label (sca-fu.com) over D1 display name (SCAFU Production)
          const label = entry.domain || entry.label || res.tenant.name;
          creds[res.tenant.id] = {
            apiKey: entry.apiKey,
            controlPlaneUrl: cp,
            label,
            domain,
          };
          importedTenants.push({
            id: res.tenant.id,
            label,
            domain,
            controlPlaneUrl: cp,
          });
          ok += 1;
          if (!firstOk) {
            firstOk = {
              session: {
                controlPlaneUrl: cp,
                apiKey: entry.apiKey,
                token: res.token,
                tenant: res.tenant,
                activeTenantId: res.tenant.id,
              },
              tenant: res.tenant,
              domain,
            };
          }
        } catch (err) {
          failed.push({
            label: displayLabel,
            error: err instanceof Error ? err.message : 'login failed',
          });
        }
      }

      saveVaultCredentials(creds);
      // Overwrite tenant list so orphans never stay in the switcher
      saveTenants(importedTenants);
      const allTenants = importedTenants;

      const activateId =
        options?.activateTenantId && creds[options.activateTenantId]
          ? options.activateTenantId
          : firstOk?.tenant.id;

      if (activateId && firstOk) {
        // Prefer requested activate; may need re-login
        let session = firstOk.session;
        let tenant = firstOk.tenant;
        if (activateId !== firstOk.tenant.id && creds[activateId]) {
          const c = creds[activateId];
          const api = new ShadowGuardApi(c.controlPlaneUrl);
          const res = await api.login(c.apiKey);
          session = {
            controlPlaneUrl: c.controlPlaneUrl,
            apiKey: c.apiKey,
            token: res.token,
            tenant: res.tenant,
            activeTenantId: activateId,
          };
          tenant = res.tenant;
        }
        const api = new ShadowGuardApi(session.controlPlaneUrl, session.token);
        saveSession(session);
        setState({
          api,
          tenant,
          isConnected: true,
          isLoading: false,
          error: failed.length
            ? `Imported ${ok}; ${failed.length} failed (see console)`
            : null,
          allTenants,
          activeTenantId: session.activeTenantId || tenant.id,
          hasSavedVault: true,
        });
        scheduleTokenRefresh(session);
        if (failed.length) console.warn('[ShadowGuard] vault import failures', failed);
      } else {
        setState({
          api: null,
          tenant: null,
          isConnected: false,
          isLoading: false,
          error: ok === 0 ? 'Vault import failed for all projects' : null,
          allTenants,
          activeTenantId: undefined,
          hasSavedVault: hasAnyVault(),
        });
      }

      return { ok, failed };
    },
    [scheduleTokenRefresh]
  );

  const connectSaved = useCallback(
    async (tenantId?: string) => {
      const creds = loadVaultCredentials();
      const ids = Object.keys(creds);
      if (ids.length === 0) {
        throw new Error('No saved projects on this browser. Import vault first.');
      }
      reconcileTenantsFromVault();
      const id = tenantId && creds[tenantId] ? tenantId : ids[0];
      await switchTenant(id);
    },
    [switchTenant]
  );

  const addTenant = useCallback((tenant: TenantInfo) => {
    const allTenants = upsertTenant(tenant);
    setState((s) => ({ ...s, allTenants }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        connect,
        disconnect,
        switchTenant,
        addTenant,
        importVault,
        connectSaved,
        clearVault,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
